import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api, fehlertext, type SessionUser } from "./api";
import { ablageAendern, holenMitAblage } from "./ablage";
import { AblageHinweis } from "./netzstand";
import { nachStempelung, type Buchung, type Tag, type Termin } from "./tagdaten";
import { alsDatumUndZeit as datumUndZeit, alsUhrzeit as uhrzeit } from "./datum";
import { sendenOderEinreihen } from "./warteschlange";
import { objektAuswahl } from "./objektname";
import { Auswahl, Eingabe, Fehler, Feld, Karte, Knopf, Laedt, Seitenkopf } from "./ui";
import { ZugangAmObjekt } from "./zugang";

/**
 * Mein Tag.
 *
 * Die einzige Maske, die ein Mitarbeiter im Alltag braucht. Sie beantwortet
 * von oben nach unten genau die Fragen, die er am Morgen hat: Laeuft mein
 * Arbeitstag? Wo muss ich heute hin? Wie komme ich da hin? Und was ist dort zu
 * tun?
 *
 * Drei Entscheidungen, die man spaeter nachlesen will:
 *
 * **Keine Auswahl, wo eine Vorgabe steht.** Der Einsatz wird am Termin
 * gestempelt, nicht an einer Objektliste. Wer aus vierzig Objekten das
 * richtige heraussuchen muss, greift irgendwann daneben, und dann steht die
 * Zeit beim falschen Kunden.
 *
 * **Ein Knopf je Zustand.** Laeuft nichts, gibt es "Arbeitstag beginnen".
 * Laeuft der Tag, gibt es je Termin "Einsatz beginnen". Laeuft ein Einsatz,
 * gibt es "Einsatz beenden" und sonst nichts. Was gerade nicht geht,
 * erscheint nicht ausgegraut, sondern gar nicht.
 *
 * **Die Ortung darf nie im Weg stehen.** Sie wird versucht, mit kurzer
 * Wartezeit, und wenn sie nichts liefert, wird trotzdem gestempelt. Ein
 * Mitarbeiter, der wegen einer Funkluecke nicht arbeiten kann, waere das
 * groessere Problem. Dass die Position fehlt, sieht das Buero.
 */

export function MeinTagSeite({ user }: { user: SessionUser }) {
  const { t } = useTranslation();
  const [daten, setDaten] = useState<Tag | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  /** Gesetzt, wenn der Tag aus der Kopie auf dem Geraet kommt. */
  const [ablageStand, setAblageStand] = useState<string | null>(null);
  /** Ohne Netz gestempelt: ein ruhiger Hinweis, kein Fehler. */
  const [gemerkt, setGemerkt] = useState(false);
  /** Es laeuft noch ein Einsatz und der naechste soll beginnen. */
  const [nachfrage, setNachfrage] = useState<{
    laufend: Buchung;
    vorschlag: string;
    fahrtMinuten: number | null;
    weiter: Record<string, unknown>;
  } | null>(null);

  const laden = useCallback(async () => {
    // Ohne Netz kommt der Tag aus der Kopie auf dem Geraet. Ohne sie stuende
    // ein Mitarbeiter im Funkloch vor einer leeren Maske.
    const { daten: geladen, stand } = await holenMitAblage<Tag>("/mein-tag");
    setDaten(geladen);
    setAblageStand(stand);
  }, []);

  useEffect(() => {
    void laden().catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }, [laden, t]);

  /**
   * Jede Stempelbewegung geht durch diese eine Stelle: senden, neu laden, und
   * die Position danach nachreichen. Vier Knoepfe mit vier eigenen Fassungen
   * davon waeren vier Gelegenheiten, die Ortung an einer Stelle zu vergessen.
   *
   * **Auf die Ortung wird nicht gewartet.** Vorher stand hier ein await auf
   * die Position, und das kostete gemessen zwei bis sechs Sekunden, im
   * schlimmsten Fall elf, wenn der Browser sich gar nicht meldet. In dieser
   * Zeit stand der Knopf grau da und nichts geschah. Wer glaubt, es sei nichts
   * passiert, drueckt ein zweites Mal.
   *
   * Fachlich ist die neue Reihenfolge auch die richtige: der Zeitpunkt ist
   * das, was das Arbeitszeitregister verlangt, und er darf nicht darauf
   * warten, dass ein Satellitensignal eintrifft. Die Entfernung zum Objekt ist
   * eine Kontrollzahl, sie kommt eine Sekunde spaeter und ist dann genauso
   * gut.
   *
   * Die Suche nach der Position startet trotzdem sofort, damit sie schon
   * laeuft, waehrend die Stempelung unterwegs ist.
   */
  /**
   * Die rote Aenderung quittieren.
   *
   * Sofort in der Anzeige, danach beim Server. Wer den Knopf drueckt und
   * zweieinhalb Sekunden auf eine Antwort wartet, drueckt ihn ein zweites Mal.
   * Ohne Netz kommt die Quittung nicht an; dann steht der Knopf nach dem
   * Neuladen wieder da, und das ist ehrlicher als eine Quittung, die das Buero
   * sieht, obwohl sie nie angekommen ist.
   */
  async function aenderungGelesen(terminId: string) {
    setDaten((alt) => (alt ? {
      ...alt,
      termine: alt.termine.map((x) => (
        x.id === terminId ? { ...x, aenderungGelesenAm: new Date().toISOString() } : x
      ))
    } : alt));
    try {
      await api.post(`/termine/${terminId}/aenderung-gelesen`);
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
      await laden().catch(() => undefined);
    }
  }

  async function stempeln(pfad: string, koerper: Record<string, unknown> = {}) {
    setLaeuft(true);
    setFehler(null);
    setGemerkt(false);
    try {
      /*
       * Ohne Netz wandert die Stempelung in die Warteschlange. Dort gibt es
       * keine Antwort des Servers, an die sich eine Position spaeter haengen
       * liesse, deshalb wird in diesem einen Fall auf sie gewartet.
       */
      const ohneNetz = typeof navigator !== "undefined" && navigator.onLine === false;
      const unterwegs = positionHolen();
      const position = ohneNetz ? await unterwegs : null;
      const zeit = new Date().toISOString();

      /*
       * Der Zeitpunkt geht nur mit, wenn ohne Netz gestempelt wird.
       *
       * Ginge er immer mit, staende jede Stempelung im Register als
       * 'app_offline', auch die mit vollem Empfang. Der Server soll die Zeit
       * setzen, solange er sie setzen kann.
       *
       * Der Vorgangsschluessel dagegen geht immer mit: er kostet nichts und
       * verhindert, dass aus einem wiederholten Versuch zwei Buchungen werden.
       */
      const vorgang = crypto.randomUUID();
      let antwort: Stempelantwort | null = null;
      const art = await sendenOderEinreihen(
        {
          art: "stempel",
          pfad,
          methode: "POST",
          koerper: { ...koerper, position, vorgang, gestempeltAm: zeit },
          beschreibung: t(`tag.stempel.${pfad.split("/").slice(2).join(".")}`),
          // 409 heisst hier: der Server hat diese Stempelung schon. Etwa das
          // Ende eines Arbeitstages, der bereits beendet ist.
          erledigtBei: [409]
        },
        async () => {
          antwort = await api.post<Stempelantwort>(pfad, { ...koerper, position, vorgang });
        }
      );

      if (art === "eingereiht") {
        /*
         * Ohne Netz: die Anzeige selbst nachfuehren und die Kopie auf dem
         * Geraet gleich mit. Sonst steht der Knopf danach unveraendert da und
         * der Mitarbeiter stempelt ein zweites Mal.
         */
        setGemerkt(true);
        setDaten((alt) => (alt ? nachStempelung(alt, pfad, koerper, zeit) : alt));
        await ablageAendern<Tag>("/mein-tag", (alt) => nachStempelung(alt, pfad, koerper, zeit));
      } else {
        await laden();
        // Nebenher, ohne den Knopf laenger zu sperren.
        void positionNachreichen(antwort, unterwegs);
      }
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  /**
   * Einen Einsatz beginnen, und vorher fragen, falls noch einer laeuft.
   *
   * Der haeufigste Fehler im Alltag: abschliessen, ins Auto, weiterfahren, und
   * das Ausstempeln vergessen. Bisher schloss die Anwendung den alten Einsatz
   * beim naechsten Stempeln stillschweigend im selben Augenblick. Der
   * vorherige Kunde bekam damit die Fahrzeit auf seine Stunden, und niemand
   * hat es je bemerkt.
   *
   * Jetzt wird gefragt, bevor der neue Einsatz beginnt, und zwar dort, wo der
   * Mitarbeiter ohnehin steht und das Telefon ohnehin in der Hand hat. Im
   * Buero ist danach nichts mehr zu korrigieren.
   */
  async function einsatzStarten(koerper: Record<string, unknown>, zielObjektId?: string | null) {
    const laufend = daten?.einsatzOffen ?? null;
    if (!laufend) {
      await stempeln("/zeiten/einsatz/start", koerper);
      return;
    }
    /*
     * Der Vorschlag kommt vom Server, weil dort die Fahrtannahmen stehen.
     * Kommt er nicht, weil das Netz fehlt, wird "jetzt" vorgeschlagen und der
     * Mitarbeiter zieht die Uhrzeit selbst zurecht. Eine fehlende Schaetzung
     * ist kein Grund, die Frage nicht zu stellen.
     */
    let vorschlag = new Date().toISOString();
    let fahrtMinuten: number | null = null;
    try {
      /*
       * Fuer die Schaetzung zaehlt das Objekt, zu dem er gerade faehrt. Beim
       * geplanten Termin steht es nicht im Koerper der Stempelung, denn dort
       * genuegt die Terminkennung; hier wird es trotzdem gebraucht.
       */
      const ziel = zielObjektId
        ?? (typeof koerper.objektId === "string" ? koerper.objektId : null);
      const a = await api.get<{ vorschlag: string; fahrtMinuten: number | null }>(
        `/zeiten/einsatz/wegfahrt${ziel ? `?objektId=${ziel}` : ""}`
      );
      vorschlag = a.vorschlag;
      fahrtMinuten = a.fahrtMinuten;
    } catch {
      // Ohne Netz bleibt es bei jetzt.
    }
    setNachfrage({ laufend, vorschlag, fahrtMinuten, weiter: koerper });
  }

  /** Das nachgetragene Ende buchen und danach den neuen Einsatz beginnen. */
  async function nachtragenUndStarten(ende: string) {
    const auftrag = nachfrage;
    if (!auftrag) return;
    setNachfrage(null);
    await stempeln("/zeiten/einsatz/ende", { ende, grund: "Beim nächsten Kunden nachgetragen" });
    await stempeln("/zeiten/einsatz/start", auftrag.weiter);
  }

  if (!daten) return <Laedt />;

  const laufenderTermin = daten.einsatzOffen?.terminId ?? null;

  return (
    <>
      <Seitenkopf titel={t("tag.titel", { name: user.displayName.split(" ")[0] })} />
      <Fehler text={fehler} />
      <AblageHinweis stand={ablageStand} />
      {gemerkt && (
        <p className="mb-4 rounded border border-marke bg-marke/10 px-3 py-2 text-sm">
          {t("netz.gemerkt")}
        </p>
      )}

      {daten.vergessen && daten.tagOffen ? (
        <Nachfrage
          tag={daten.tagOffen}
          laeuft={laeuft}
          onNachtragen={(ende, grund) => void stempeln("/zeiten/tag/ende", { ende, grund })}
        />
      ) : (
        <Tageskopf
          daten={daten}
          laeuft={laeuft}
          onStart={() => void stempeln("/zeiten/tag/start")}
          onEnde={() => void stempeln("/zeiten/tag/ende")}
          onPause={() => void stempeln("/zeiten/pause")}
          onPauseEnde={() => void stempeln("/zeiten/pause/ende")}
        />
      )}

      <h2 className="mt-8 text-lg font-bold">{t("tag.termine")}</h2>
      {daten.termine.length === 0 ? (
        <Karte className="mt-3 p-6 text-center text-leise">{t("tag.keineTermine")}</Karte>
      ) : (
        <ul className="mt-3 space-y-3">
          {daten.termine.map((termin) => termin.art === "pause" ? (
            /*
             * Die geplante Pause steht im Tag, damit der Mitarbeiter sieht,
             * wann sie vorgesehen ist. Knoepfe hat sie keine: gestempelt wird
             * die Pause oben am Arbeitstag, und zwei Wege fuer dieselbe
             * Buchung waeren einer zu viel.
             */
            <li key={termin.id}>
              <Karte className="border border-dashed border-linie/50 bg-transparent p-4">
                <div className="flex flex-wrap items-baseline gap-x-3">
                  <span className="text-lg font-bold tabular-nums">{termin.von}</span>
                  <span className="text-sm text-leise">bis {termin.bis}</span>
                  <span className="text-lg font-bold">{t("plan.pause")}</span>
                </div>
                <p className="mt-1 text-sm text-leise">{t("tag.pausePlan")}</p>
              </Karte>
            </li>
          ) : (
            <Terminkarte
              key={termin.id}
              termin={termin}
              ichId={user.id}
              erledigt={daten.buchungen.some(
                (b) => b.terminId === termin.id && b.ende !== null
              )}
              laeuft={laufenderTermin === termin.id}
              /*
               * Frueher war der Knopf gesperrt, solange irgendwo noch ein
               * Einsatz lief. Wer beim naechsten Kunden stand, musste erst
               * zurueckblaettern und den alten beenden, und das stempelte das
               * Ende auf jetzt, also nach der Fahrt. Genau so entstand die zu
               * lange Zeit beim vorherigen Kunden. Jetzt ist der Knopf offen
               * und die Anwendung fragt nach dem richtigen Ende.
               */
              onGelesen={() => void aenderungGelesen(termin.id)}
              gesperrt={laeuft}
              inPause={Boolean(daten.tagOffen?.pauseOffen)}
              onStart={() => void einsatzStarten({ terminId: termin.id }, termin.objektId)}
              onEnde={() => void stempeln("/zeiten/einsatz/ende")}
            />
          ))}
        </ul>
      )}

      <Ungeplant
        laufend={daten.einsatzOffen}
        gesperrt={laeuft || Boolean(daten.tagOffen?.pauseOffen)}
        onStart={(objektId) => void einsatzStarten({ objektId })}
        onEnde={() => void stempeln("/zeiten/einsatz/ende")}
      />

      <Tagesliste buchungen={daten.buchungen} />

      {nachfrage && (
        <Wegfahrt
          laufend={nachfrage.laufend}
          vorschlag={nachfrage.vorschlag}
          fahrtMinuten={nachfrage.fahrtMinuten}
          laeuft={laeuft}
          onAbbruch={() => setNachfrage(null)}
          onNachtragen={(ende) => void nachtragenUndStarten(ende)}
        />
      )}
    </>
  );
}

/**
 * Die Frage nach dem vergessenen Ende.
 *
 * Sie kommt im Augenblick des naechsten Einstempelns, denn dort faellt es auf
 * und dort ist der Mitarbeiter am Geraet. Vorbelegt ist der Zeitpunkt, an dem
 * er nach der geschaetzten Fahrt losgefahren sein muesste; bestaetigen ist ein
 * Tipp, korrigieren zwei.
 *
 * Der zweite Knopf sagt "war bis eben dort" und setzt schlicht jetzt. Ohne ihn
 * muesste jemand, der wirklich gerade erst gegangen ist, den Vorschlag von
 * Hand zurueckdrehen, und das taete er beim dritten Mal nicht mehr.
 */
function Wegfahrt({
  laufend, vorschlag, fahrtMinuten, laeuft, onAbbruch, onNachtragen
}: {
  laufend: Buchung;
  vorschlag: string;
  fahrtMinuten: number | null;
  laeuft: boolean;
  onAbbruch: () => void;
  onNachtragen: (ende: string) => void;
}) {
  const { t } = useTranslation();
  const [zeit, setZeit] = useState(uhrzeit(vorschlag));

  /** Aus "14:35" wird ein Zeitpunkt am Tag des Einsatzbeginns. */
  function alsZeitpunkt(wert: string): string | null {
    const [stunde, minute] = wert.split(":").map(Number);
    if (!Number.isFinite(stunde) || !Number.isFinite(minute)) return null;
    const d = new Date(laufend.beginn);
    d.setHours(stunde, minute, 0, 0);
    return d.toISOString();
  }

  const gewaehlt = alsZeitpunkt(zeit);
  const gueltig = gewaehlt !== null
    && new Date(gewaehlt).getTime() > new Date(laufend.beginn).getTime()
    && new Date(gewaehlt).getTime() <= Date.now() + 60_000;

  return (
    <div className="fixed inset-0 z-30 flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <Karte className="w-full max-w-md p-5">
        <h2 className="text-lg font-bold">{t("tag.wegfahrtTitel")}</h2>
        <p className="mt-1 text-sm">
          {t("tag.wegfahrtText", {
            objekt: laufend.objekt ?? "?",
            seit: uhrzeit(laufend.beginn)
          })}
        </p>
        {fahrtMinuten !== null && (
          <p className="mt-1 text-sm text-leise">
            {t("tag.wegfahrtFahrt", { count: fahrtMinuten })}
          </p>
        )}

        <div className="mt-4">
          <Feld label={t("tag.wegfahrtFrage")}>
            <Eingabe type="time" value={zeit} onChange={(e) => setZeit(e.target.value)} />
          </Feld>
        </div>
        {!gueltig && <p className="mt-1 text-sm text-red-700">{t("tag.wegfahrtUngueltig")}</p>}

        <div className="mt-5 flex flex-wrap gap-2">
          <Knopf
            disabled={laeuft || !gueltig}
            onClick={() => gewaehlt && onNachtragen(gewaehlt)}
          >
            {t("tag.wegfahrtUebernehmen")}
          </Knopf>
          <Knopf
            art="neben"
            disabled={laeuft}
            onClick={() => onNachtragen(new Date().toISOString())}
          >
            {t("tag.wegfahrtEben")}
          </Knopf>
          <Knopf art="neben" disabled={laeuft} onClick={onAbbruch}>
            {t("aktion.abbrechen")}
          </Knopf>
        </div>
      </Karte>
    </div>
  );
}

/** Der Kopf: Zustand des Arbeitstages und die dazu passenden Knoepfe. */
function Tageskopf({
  daten, laeuft, onStart, onEnde, onPause, onPauseEnde
}: {
  daten: Tag; laeuft: boolean;
  onStart: () => void; onEnde: () => void; onPause: () => void; onPauseEnde: () => void;
}) {
  const { t } = useTranslation();
  const jetzt = useTicker(Boolean(daten.tagOffen));

  if (!daten.tagOffen) {
    return (
      <Karte className="p-6 text-center">
        <p className="text-sm text-leise">{t("tag.nichtBegonnen")}</p>
        <button
          disabled={laeuft}
          onClick={onStart}
          className="mt-4 w-full rounded-card bg-marke px-8 py-6 text-xl font-bold text-white transition hover:bg-marke-dunkel disabled:opacity-60 sm:w-auto"
        >
          {t("tag.beginnen")}
        </button>
        {daten.heuteSekunden > 0 && (
          <p className="mt-4 text-sm text-leise">
            {t("zeit.heuteBisher", { dauer: dauertext(daten.heuteSekunden) })}
          </p>
        )}
      </Karte>
    );
  }

  const offen = daten.tagOffen;
  const laufend = Math.max(
    0,
    Math.round((jetzt - new Date(offen.beginn).getTime()) / 1000) - offen.pausenSekunden
  );

  /*
   * Die Pause erinnert und wird nicht gebucht.
   *
   * Der Plan sieht sie vor, das Register nach Art. 34.9 ET zeigt aber nur, was
   * gestempelt wurde. Eine Anwendung, die dreissig Minuten Pause abzieht, ohne
   * dass jemand sie gemacht hat, schreibt eine Unwahrheit in genau das
   * Dokument, in das eine Inspektion zuerst sieht. Also ein Hinweis neben dem
   * Knopf, mehr nicht.
   */
  const pauseFaellig =
    typeof daten.pauseNachMinuten === "number"
    && !offen.pauseOffen
    && offen.pausenSekunden === 0
    && laufend >= daten.pauseNachMinuten * 60;

  return (
    <Karte className="p-6">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="text-4xl font-bold tabular-nums">{dauertext(laufend)}</span>
        <span className="text-sm text-leise">{t("zeit.seit", { zeit: uhrzeit(offen.beginn) })}</span>
        {offen.pauseOffen && (
          <span className="rounded bg-marke/30 px-2 py-0.5 text-xs font-semibold">
            {t("zeit.inPause")}
          </span>
        )}
      </div>
      {daten.einsatzOffen && (
        <p className="mt-2 text-sm font-semibold text-verweis">
          {t("tag.imEinsatz", {
            objekt: daten.einsatzOffen.objekt ?? "?",
            seit: uhrzeit(daten.einsatzOffen.beginn)
          })}
        </p>
      )}
      {pauseFaellig && (
        <p className="mt-3 rounded border border-marke bg-marke/10 px-3 py-2 text-sm font-semibold">
          {t("zeit.pauseFaellig", { stunden: Math.floor(laufend / 3600) })}
        </p>
      )}
      <div className="mt-5 flex flex-wrap gap-2">
        {offen.pauseOffen ? (
          <Knopf disabled={laeuft} onClick={onPauseEnde}>{t("zeit.pauseEnde")}</Knopf>
        ) : (
          <Knopf art="neben" disabled={laeuft} onClick={onPause}>{t("zeit.pause")}</Knopf>
        )}
        <Knopf art="warnung" disabled={laeuft} onClick={onEnde}>{t("tag.beenden")}</Knopf>
      </div>
    </Karte>
  );
}

/**
 * Die Checkliste zu einem Objekt beginnen.
 *
 * Ein Bauteil fuer beide Wege, den geplanten Termin und den ungeplanten
 * Einsatz. Vorher gab es sie nur am Termin, und wer spontan hinfuhr, konnte
 * nicht pruefen; er haette den Bericht am Abend im Buero nachtragen muessen,
 * also gar nicht.
 *
 * **Erst auf Druck geladen.** Die Listen eines Objekts holt sie beim ersten
 * Tippen und nicht beim Anzeigen. Bei sechs Terminen am Tag waeren das sechs
 * Abfragen, die auf einem Telefon mit einem Balken Empfang jede fuer sich
 * spuerbar ist, und in den meisten Faellen braucht sie niemand.
 *
 * **Gibt es mehrere, entscheidet der Mensch.** Dann erscheint die Auswahl an
 * Ort und Stelle, statt dass die Maske auf die Objektseite springt. Wer am
 * Objekt steht, will pruefen und nicht navigieren.
 */
function Checklistenknopf({
  objektId, vorgabeId, vorgang, terminId
}: {
  objektId: string;
  /** Die am Termin hinterlegte Liste. Sie schlaegt jede Auswahl. */
  vorgabeId?: string | null;
  /** Schluessel gegen Doppelanlage, wenn jemand zweimal tippt. */
  vorgang: string;
  /**
   * Der Termin, zu dem der Bericht gehoert. Fehlt er, gehoert der Bericht dem
   * Ersteller allein; das ist der spontane Einsatz ohne Plan.
   */
  terminId?: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [auswahl, setAuswahl] = useState<{ id: string; label: string | null }[] | null>(null);
  const [gewaehlt, setGewaehlt] = useState("");

  async function beginnen(checklisteId: string) {
    const res = await api.post<{ id: string }>("/inspections", {
      propertyChecklistId: checklisteId,
      /*
       * Das Objekt wird ausdruecklich mitgeschickt.
       *
       * Bei einer Objektcheckliste steht es ohnehin an der Liste und der Wert
       * wird nicht gebraucht. Bei einer Berichtsvorlage ist er die ganze
       * Zuordnung: die Vorlage haengt an keinem Haus, und ohne diese Angabe
       * wuesste der Server nicht, ueber welches Objekt berichtet wird.
       */
      propertyId: objektId,
      terminId,
      clientRef: vorgang
    });
    navigate(`/pruefungen/${res.id}`);
  }

  async function oeffnen() {
    setLaeuft(true);
    setFehler(null);
    try {
      if (vorgabeId) {
        await beginnen(vorgabeId);
        return;
      }
      /*
       * Zwei Quellen, eine Auswahl.
       *
       * Am Objekt haengen die Checklisten dieses Hauses, im Betrieb stehen die
       * Berichtsvorlagen. Ein Hausbetreuer hat nur das erste, ein Fensterbauer
       * nur das zweite, und ein Betrieb mit beidem soll sich nicht erst
       * entscheiden muessen, in welcher der beiden Listen er sucht. Deshalb
       * werden beide geholt und zusammengelegt; die Checklisten des Objekts
       * stehen vorn, denn wo es sie gibt, sind sie die genauere Antwort.
       */
      const [amObjekt, vorlagen] = await Promise.all([
        api.get<{ checklists: { id: string; label: string | null; is_active: boolean }[] }>(
          `/properties/${objektId}/checklists`
        ),
        api.get<{ vorlagen: { id: string; label: string | null }[] }>("/berichtsvorlagen")
          // Ohne Vorlagen geht es mit den Objektlisten weiter. Ein Fehler hier
          // darf das Pruefen am Objekt nicht verhindern.
          .catch(() => ({ vorlagen: [] as { id: string; label: string | null }[] }))
      ]);
      const aktiv = [
        ...amObjekt.checklists.filter((c) => c.is_active).map((c) => ({ id: c.id, label: c.label })),
        ...vorlagen.vorlagen.map((v) => ({ id: v.id, label: v.label }))
      ];
      if (aktiv.length === 0) {
        setFehler(t("tag.keineCheckliste"));
        return;
      }
      if (aktiv.length === 1) {
        await beginnen(aktiv[0].id);
        return;
      }
      setAuswahl(aktiv);
      setGewaehlt(aktiv[0].id);
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  if (auswahl) {
    return (
      <div className="flex flex-wrap items-end gap-2">
        <Feld label={t("tag.checklisteWaehlen")}>
          <Auswahl value={gewaehlt} onChange={(e) => setGewaehlt(e.target.value)}>
            {auswahl.map((c) => (
              <option key={c.id} value={c.id}>{c.label ?? t("tag.checkliste")}</option>
            ))}
          </Auswahl>
        </Feld>
        <div className="flex gap-2 pb-1">
          <Knopf
            disabled={laeuft || !gewaehlt}
            onClick={() => {
              setLaeuft(true);
              void beginnen(gewaehlt)
                .catch((e) => setFehler(fehlertext(e, t("fehler.speichern"))))
                .finally(() => setLaeuft(false));
            }}
          >
            {t("tag.checkliste")}
          </Knopf>
          <Knopf art="neben" onClick={() => setAuswahl(null)}>{t("aktion.abbrechen")}</Knopf>
        </div>
        <Fehler text={fehler} />
      </div>
    );
  }

  return (
    <>
      <Knopf art="neben" disabled={laeuft} onClick={() => void oeffnen()}>
        {t("tag.checkliste")}
      </Knopf>
      <Fehler text={fehler} />
    </>
  );
}

/** Ein Kundentermin mit allem, was daran zu tun ist. */
function Terminkarte({
  termin, ichId, erledigt, laeuft, gesperrt, inPause, onStart, onEnde, onGelesen
}: {
  termin: Termin; ichId: string;
  erledigt: boolean; laeuft: boolean; gesperrt: boolean; inPause: boolean;
  onStart: () => void; onEnde: () => void; onGelesen: () => void;
}) {
  const { t } = useTranslation();
  const mitfahrer = (termin.mitglieder ?? []).filter((m) => m.id !== ichId);

  const route = termin.ziel
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(termin.ziel)}`
    : null;

  return (
    <li>
      <Karte className={`p-4 ${laeuft ? "border-l-4 border-marke" : erledigt ? "opacity-70" : ""}`}>
        {/*
          * Die Aenderung steht ganz oben, vor der Uhrzeit.
          *
          * Wer den Termin aufschlaegt, liest zuerst, was heute anders ist, und
          * erst danach, wann und wo. Stuende sie unten bei den Bemerkungen,
          * waere sie genau dann ueberlesen, wenn sie zaehlt: morgens, im
          * Vorbeigehen, mit dem Autoschluessel in der Hand.
          */}
        {termin.aenderung && (
          <div className="mb-3 rounded border-s-4 border-red-700 bg-red-50 p-3">
            <p className="text-sm font-bold text-red-800">{t("tag.aenderung")}</p>
            <p className="mt-1 whitespace-pre-line text-sm text-red-900">{termin.aenderung}</p>
            {termin.aenderungGelesenAm ? (
              <p className="mt-2 text-xs text-red-800">{t("tag.aenderungQuittiert")}</p>
            ) : (
              <Knopf art="warnung" onClick={onGelesen}>{t("tag.aenderungGelesen")}</Knopf>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-lg font-bold tabular-nums">{termin.von}</span>
          <span className="text-sm text-leise">bis {termin.bis}</span>
          {/*
            Zuerst der Mensch, dann das Haus. Wer an der Tuer steht, muss
            wissen, wie er den Eigentuemer anspricht; wo er ist, sieht er.
          */}
          <span className="text-lg font-bold">
            {termin.ansprache ?? termin.objekt ?? t("zeit.ohneObjekt")}
          </span>
          {termin.ansprache && termin.objekt && (
            <span className="text-sm text-leise">{termin.objekt}</span>
          )}
          {laeuft && (
            <span className="rounded bg-marke px-2 py-0.5 text-xs font-bold text-white">
              {t("tag.laeuft")}
            </span>
          )}
          {erledigt && !laeuft && (
            <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-900">
              {t("tag.erledigt")}
            </span>
          )}
        </div>
        {termin.anschrift && <p className="mt-1 text-sm text-leise">{termin.anschrift}</p>}
        {/*
          * Wer sonst noch mitfaehrt. Vier Stunden Garten koennen zwei Gaertner
          * zwei Stunden sein, und wer das erst am Objekt merkt, hat entweder
          * gewartet oder dieselbe Arbeit zweimal gemacht.
          */}
        {mitfahrer.length > 0 && (
          <p className="mt-1 text-sm font-semibold text-verweis">
            {t("plan.gemeinsamMit", { namen: mitfahrer.map((m) => m.name).join(", ") })}
          </p>
        )}
        {termin.notiz && <p className="mt-1 text-sm">{termin.notiz}</p>}

        <div className="mt-4 flex flex-wrap gap-2">
          {route && (
            <a
              href={route}
              target="_blank"
              rel="noreferrer"
              className="rounded border border-linie/40 px-4 py-2 text-sm font-semibold transition hover:bg-grund"
            >
              {t("tag.navigieren")}
            </a>
          )}
          {laeuft ? (
            <Knopf art="warnung" onClick={onEnde}>{t("tag.einsatzEnde")}</Knopf>
          ) : (
            !inPause && (
              <Knopf disabled={gesperrt} onClick={onStart}>{t("tag.einsatzStart")}</Knopf>
            )
          )}
          {termin.objektId && (
            <Checklistenknopf
              objektId={termin.objektId}
              vorgabeId={termin.checklisteId}
              vorgang={`termin-${termin.id}`}
              terminId={termin.id}
            />
          )}
        </div>

        {/* Schluessel, Tor und Alarm direkt am Termin. Wer davorsteht, soll
            nicht erst in die Objektakte blaettern muessen. Die Karte erscheint
            nur, wenn die API den Zugriff auch erlaubt. */}
        {termin.objektId && (
          <div className="mt-4">
            <ZugangAmObjekt objektId={termin.objektId} flach />
          </div>
        )}
      </Karte>
    </li>
  );
}

/**
 * Der ungeplante Einsatz.
 *
 * Steht bewusst unten und zugeklappt. Der Normalweg ist der Termin aus dem
 * Plan; das hier ist der Notfall am Samstag. Wer ihn benutzt, erscheint im
 * Backoffice als ungeplant, damit das Buero es einordnen kann.
 */
function Ungeplant({
  laufend, gesperrt, onStart, onEnde
}: {
  laufend: Buchung | null; gesperrt: boolean;
  onStart: (objektId: string) => void; onEnde: () => void;
}) {
  const { t } = useTranslation();
  const [offen, setOffen] = useState(false);
  const [objekte, setObjekte] = useState<{ id: string; name: string; customer_name: string }[]>([]);
  const [objektId, setObjektId] = useState("");

  useEffect(() => {
    if (!offen || objekte.length > 0) return;
    void api.get<{ properties: { id: string; name: string; customer_name: string }[] }>("/properties")
      .then((a) => setObjekte(a.properties))
      .catch(() => setObjekte([]));
  }, [offen, objekte.length]);

  // Laeuft gerade ein ungeplanter Einsatz, gehoert der Beenden-Knopf hierher.
  if (laufend && !laufend.terminId) {
    return (
      <Karte className="mt-6 border-l-4 border-marke p-4">
        <p className="font-semibold">
          {t("tag.ungeplantLaeuft", { objekt: laufend.objekt ?? "?", seit: uhrzeit(laufend.beginn) })}
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          {/*
            * Auch der spontane Einsatz bekommt seine Checkliste. Wer wegen
            * eines Wasserschadens hinfaehrt und dabei den Pool mitmacht, soll
            * den Bericht dort schreiben, wo er steht, und nicht abends im
            * Buero aus dem Gedaechtnis.
            */}
          {laufend.objektId && (
            <Checklistenknopf objektId={laufend.objektId} vorgang={`einsatz-${laufend.id}`} />
          )}
          <Knopf art="warnung" disabled={gesperrt} onClick={onEnde}>{t("tag.einsatzEnde")}</Knopf>
        </div>
      </Karte>
    );
  }

  if (laufend) return null;

  return (
    <div className="mt-6">
      {!offen ? (
        <button className="text-sm text-leise underline" onClick={() => setOffen(true)}>
          {t("tag.ungeplantOeffnen")}
        </button>
      ) : (
        <Karte className="p-4">
          <p className="text-sm text-leise">{t("tag.ungeplantHinweis")}</p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <Feld label={t("zeit.objektOptional")}>
              <Auswahl value={objektId} onChange={(e) => setObjektId(e.target.value)}>
                <option value="">…</option>
                {objektAuswahl(objekte).map(({ objekt, beschriftung }) => (
                  <option key={objekt.id} value={objekt.id}>{beschriftung}</option>
                ))}
              </Auswahl>
            </Feld>
            <div className="pb-1 flex gap-2">
              <Knopf disabled={gesperrt || !objektId} onClick={() => onStart(objektId)}>
                {t("tag.einsatzStart")}
              </Knopf>
              <Knopf art="neben" onClick={() => setOffen(false)}>{t("aktion.abbrechen")}</Knopf>
            </div>
          </div>
        </Karte>
      )}
    </div>
  );
}

/** Was heute schon gestempelt wurde. Nur zum Nachsehen, ohne Knoepfe. */
function Tagesliste({ buchungen }: { buchungen: Buchung[] }) {
  const { t } = useTranslation();
  const einsaetze = buchungen.filter((b) => b.art === "assignment");
  if (einsaetze.length === 0) return null;

  return (
    <>
      <h2 className="mt-8 text-lg font-bold">{t("tag.gestempelt")}</h2>
      <ul className="mt-3 space-y-2">
        {einsaetze.map((b) => (
          <li key={b.id} className="flex flex-wrap items-baseline gap-x-3 rounded-card bg-white p-3 text-sm">
            <span className="tabular-nums font-semibold">
              {uhrzeit(b.beginn)} bis {b.ende ? uhrzeit(b.ende) : "…"}
            </span>
            <span>{b.objekt ?? "—"}</span>
            {b.geplant === false && (
              <span className="rounded bg-grund px-2 py-0.5 text-xs font-semibold">
                {t("tag.ungeplant")}
              </span>
            )}
            <span className="ms-auto tabular-nums text-leise">{dauertext(b.sekunden)}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

/** Der rote Kasten fuer den vergessenen Feierabend. */
function Nachfrage({
  tag, laeuft, onNachtragen
}: { tag: Buchung; laeuft: boolean; onNachtragen: (ende: string, grund: string) => void }) {
  const { t } = useTranslation();
  const [ende, setEnde] = useState(() => fuerEingabe(tag.beginn, 8));
  const [grund, setGrund] = useState("");

  return (
    <Karte className="border-l-4 border-red-600 p-6">
      <p className="font-bold text-red-700">{t("zeit.vergessenTitel")}</p>
      <p className="mt-1 text-sm text-leise">
        {t("zeit.vergessenText", { beginn: datumUndZeit(tag.beginn) })}
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Feld label={t("zeit.echtesEnde")}>
          <Eingabe type="datetime-local" value={ende} onChange={(e) => setEnde(e.target.value)} />
        </Feld>
        <Feld label={t("zeit.grund")}>
          <Eingabe value={grund} onChange={(e) => setGrund(e.target.value)} />
        </Feld>
      </div>
      <div className="mt-4">
        <Knopf
          disabled={laeuft || !ende}
          onClick={() => onNachtragen(new Date(ende).toISOString(), grund)}
        >
          {t("zeit.nachtragen")}
        </Knopf>
      </div>
    </Karte>
  );
}

// ---------------------------------------------------------------------
// Ortung und Kleinkram
// ---------------------------------------------------------------------

/**
 * Die Position, wenn es sie gibt.
 *
 * Nie ein Fehler, immer eine Antwort. Zehn Sekunden Wartezeit, danach ohne
 * Position weiter. Ein Stempel, der auf das Satellitensignal wartet, waehrend
 * der Mitarbeiter in der Hofeinfahrt steht, ist ein kaputter Stempel.
 *
 * enableHighAccuracy verlangt das Satellitensignal statt der Funkzelle. Es
 * dauert laenger, aber ein Ortsfehler von tausend Metern waere hier genau die
 * Groesse, um die es geht.
 */
/** Was der Server auf eine Stempelung antwortet, soweit es hier zaehlt. */
interface Stempelantwort {
  id?: string;
  positionSeite?: "start" | "ende";
}

/**
 * Die Position nachreichen, nachdem gestempelt wurde.
 *
 * Laeuft bewusst nebenher und ohne Rueckmeldung an den Mitarbeiter. Er kann
 * nichts daran aendern, ob sein Telefon einen Satelliten findet, und eine
 * Fehlermeldung dafuer waere eine Meldung ohne Handlung. Dass die Entfernung
 * fehlt, sieht das Buero in der Uebersicht.
 *
 * Der Server nimmt den Nachtrag nur fuer die eigene Buchung an, nur fuer die
 * Seite, die noch keine Entfernung traegt, und nur innerhalb von zehn Minuten.
 */
async function positionNachreichen(
  antwort: Stempelantwort | null,
  unterwegs: Promise<{ lat: number; lng: number; genauigkeit: number | null } | null>
): Promise<void> {
  if (!antwort?.id || !antwort.positionSeite) return;
  const position = await unterwegs;
  if (!position) return;
  await api
    .post(`/zeiten/${antwort.id}/position`, { seite: antwort.positionSeite, position })
    .catch(() => undefined);
}

async function positionHolen(): Promise<
  { lat: number; lng: number; genauigkeit: number | null } | null
> {
  if (typeof navigator === "undefined" || !navigator.geolocation) return null;
  return new Promise((auf) => {
    let erledigt = false;
    const fertig = (
      wert: { lat: number; lng: number; genauigkeit: number | null } | null
    ) => {
      if (erledigt) return;
      erledigt = true;
      auf(wert);
    };
    navigator.geolocation.getCurrentPosition(
      (p) => fertig({
        lat: p.coords.latitude,
        lng: p.coords.longitude,
        genauigkeit: Number.isFinite(p.coords.accuracy) ? Math.round(p.coords.accuracy) : null
      }),
      () => fertig(null),
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 30_000 }
    );
    // Zweiter Riegel: manche Browser rufen bei verweigerter Berechtigung
    // weder die eine noch die andere Rueckmeldung auf.
    window.setTimeout(() => fertig(null), 11_000);
  });
}

function useTicker(aktiv: boolean): number {
  const [jetzt, setJetzt] = useState(() => Date.now());
  useEffect(() => {
    if (!aktiv) return;
    const t = window.setInterval(() => setJetzt(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [aktiv]);
  return jetzt;
}

function dauertext(sekunden: number): string {
  const s = Math.max(0, Math.round(sekunden));
  return `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")} h`;
}

function fuerEingabe(iso: string, plusStunden: number): string {
  const d = new Date(new Date(iso).getTime() + plusStunden * 3_600_000);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}
